import { useState, useEffect, useCallback } from 'react';
import { LANGUAGES } from '../lib/constants';
import { 
  CheckinTranslationDictionary, 
  ENGLISH_CHECKIN_DICTIONARY, 
  getCheckinDictionarySync, 
  fetchAICheckinTranslation, 
  OPTION_KEY_TO_ENGLISH, 
  ENGLISH_TO_OPTION_KEY 
} from '../lib/checkinTranslations';

export const useCheckinTranslation = (userLanguage: string = 'en') => {
  const [selectedLang, setSelectedLang] = useState<string>(userLanguage || 'en');
  const [showOriginal, setShowOriginal] = useState<boolean>(false);
  const [dictionary, setDictionary] = useState<CheckinTranslationDictionary>(() => 
    getCheckinDictionarySync(userLanguage || 'en')
  );
  const [isTranslating, setIsTranslating] = useState<boolean>(false);

  // Sync if user profile language changes
  useEffect(() => {
    if (userLanguage) {
      setSelectedLang(userLanguage);
      setDictionary(getCheckinDictionarySync(userLanguage));
    }
  }, [userLanguage]);

  // If selected language is not English, ensure full translation is loaded/cached
  useEffect(() => {
    if (!selectedLang || selectedLang === 'en') {
      setDictionary(ENGLISH_CHECKIN_DICTIONARY);
      return;
    }

    let isMounted = true;
    setIsTranslating(true);
    fetchAICheckinTranslation(selectedLang).then(dict => {
      if (isMounted) {
        setDictionary(dict);
        setIsTranslating(false);
      }
    }).catch(() => {
      if (isMounted) setIsTranslating(false);
    });

    return () => {
      isMounted = false;
    };
  }, [selectedLang]);

  const toggleShowOriginal = useCallback(() => {
    setShowOriginal(prev => !prev);
  }, []);

  // Active dictionary taking showOriginal into account
  const activeDict = showOriginal ? ENGLISH_CHECKIN_DICTIONARY : dictionary;

  const tCheckin = useCallback((key: keyof CheckinTranslationDictionary | string, fallback?: string): string => {
    if (activeDict && (key in activeDict)) {
      return (activeDict as any)[key] || fallback || key;
    }
    return (ENGLISH_CHECKIN_DICTIONARY as any)[key] || fallback || key;
  }, [activeDict]);

  // Translate past responses or options
  const getTranslatedOption = useCallback((optKeyOrVal: string): string => {
    if (!optKeyOrVal) return '';
    if (showOriginal) {
      if (optKeyOrVal in OPTION_KEY_TO_ENGLISH) return OPTION_KEY_TO_ENGLISH[optKeyOrVal];
      return optKeyOrVal;
    }
    
    // Check if it's an option key like opt_always
    if (optKeyOrVal in activeDict) {
      return (activeDict as any)[optKeyOrVal];
    }

    // Check if it's an English text like "Always"
    const lower = optKeyOrVal.toLowerCase().trim();
    const optKey = ENGLISH_TO_OPTION_KEY[lower];
    if (optKey && optKey in activeDict) {
      return (activeDict as any)[optKey];
    }

    return optKeyOrVal;
  }, [activeDict, showOriginal]);

  const targetLangLabel = LANGUAGES.find(l => l.code === selectedLang)?.label || selectedLang;
  const isTranslated = selectedLang !== 'en' && !showOriginal;

  return {
    selectedLang,
    setSelectedLang,
    showOriginal,
    setShowOriginal,
    toggleShowOriginal,
    isTranslating,
    isTranslated,
    targetLangLabel,
    tCheckin,
    getTranslatedOption,
    dictionary: activeDict,
  };
};
